import { ref, computed, watch, Ref } from "vue";

interface UseVaccinationFormOptions {
  allowZeroVaccinated?: boolean;
  enforceWeightVaccinatedLink?: boolean;
}

export function useVaccinationForm(options: UseVaccinationFormOptions = {}) {
  const {
    allowZeroVaccinated = false,
    enforceWeightVaccinatedLink = true,
  } = options;
  const minimumVaccinated = allowZeroVaccinated ? 0 : 1;

  // ===================================================================
  // STATE
  // ===================================================================
  const vaccinated = ref<number>(1);
  const weight = ref<number>(0);
  const comment = ref<string>("");
  const successMessage: Ref<string> = ref("");
  const isProgrammaticChange: Ref<boolean> = ref(false);

  // ===================================================================
  // COMPUTED
  // ===================================================================
  const isWeightDisabled = computed(
    () => Number.isFinite(vaccinated.value) && vaccinated.value > 1
  );
  const isVaccinatedDisabled = computed(
    () => enforceWeightVaccinatedLink && weight.value > 0 && vaccinated.value === 1
  );
  const canSave = computed(
    () => Number.isFinite(vaccinated.value) && vaccinated.value >= minimumVaccinated
  );

  // ===================================================================
  // ACTIONS - Form Operations
  // ===================================================================
  const incrementVaccinated = () => {
    if (isVaccinatedDisabled.value) return;
    const currentVaccinated = Number.isFinite(vaccinated.value)
      ? vaccinated.value
      : minimumVaccinated;
    vaccinated.value = currentVaccinated + 1;
    validateVaccinated();
  };

  const decrementVaccinated = () => {
    if (isVaccinatedDisabled.value) return;
    if (Number.isFinite(vaccinated.value) && vaccinated.value > minimumVaccinated) {
      vaccinated.value--;
      validateVaccinated();
    }
  };

  const resetForm = () => {
    vaccinated.value = 1;
    weight.value = 0;
    comment.value = "";
    successMessage.value = "✓";
    isProgrammaticChange.value = true;
  };

  // ===================================================================
  // VALIDATORS - Form Rules
  // ===================================================================
  const validateVaccinated = () => {
    if (Number.isFinite(vaccinated.value) && vaccinated.value > 1) {
      weight.value = 0;
    }
  };

  // ===================================================================
  // WATCHERS - Cross-field Dependencies
  // ===================================================================
  watch([vaccinated, weight, comment], () => {
    if (!isProgrammaticChange.value) {
      successMessage.value = "";
    }
    isProgrammaticChange.value = false;
  });

  watch(
    () => weight.value,
    (newWeight) => {
      if (enforceWeightVaccinatedLink && Number.isFinite(newWeight) && newWeight > 0) {
        vaccinated.value = 1;
      }
    }
  );

  return {
    // ===================================================================
    // STATE
    // ===================================================================
    vaccinated,
    weight,
    comment,
    successMessage,
    isProgrammaticChange,

    // ===================================================================
    // VALIDATION
    // ===================================================================
    isWeightDisabled,
    isVaccinatedDisabled,
    canSave,

    // ===================================================================
    // ACTIONS
    // ===================================================================
    incrementVaccinated,
    decrementVaccinated,
    resetForm,
    validateVaccinated
  };
}
